/**
 * Share-card templates: the editable copy layer for shareable cards.
 *
 * Defaults live HERE in code (single source of truth, works offline, no seed
 * step). The `shareCardTemplates` Firestore collection holds admin-authored
 * overrides keyed by template id; clients merge overrides over defaults.
 *
 * Templates control COPY and TONE only. What data a card may carry (counts /
 * percentages / directions — never PII, and dollar amounts only via the
 * explicit 'detailed' amount mode), which moments are share-worthy, and which
 * visualization renders all stay code-enforced in the moment builders.
 * Rendered strings are additionally passed through scrubAmounts as
 * defense-in-depth; 'private' mode (the default) strips anything that looks
 * like a currency amount.
 *
 * Variables use the app-wide {{variable}} convention (docs/guides/VARIABLE_SYSTEM.md).
 */

import type { ShareAmountMode } from './shareCardTypes';

export type ShareCardTone = 'note' | 'celebrate' | 'gentle' | 'social';

/** Tone → accent hex (brand palette; social maps to foliage — twig is too
 *  light to carry viz elements on a sand card at export scale). */
export const SHARE_TONE_ACCENTS: Record<ShareCardTone, string> = {
  note: '#516B94', // sky
  celebrate: '#C5A059', // foliage
  gentle: '#CD7857', // clay
  social: '#C5A059', // foliage
};

export type ShareCardTemplateId =
  | 'insight_streak'
  | 'insight_spend_comparison'
  | 'insight_milestone'
  | 'insight_rank'
  | 'goal_category_spend'
  | 'goal_total_spend'
  | 'goal_total_debt'
  | 'goal_specific_debt_payoff'
  | 'goal_no_new_debt'
  | 'goal_regret_ratio'
  | 'goal_app_sessions'
  | 'goal_emergency_fund'
  | 'goal_monthly_savings'
  | 'goal_life_event'
  | 'pause_breaks'
  | 'pause_dismissed';

/** The admin-editable fields (what a Firestore override may carry). */
export interface ShareCardTemplateContent {
  eyebrow: string;
  /** '\n' marks intentional line breaks; {{variables}} substituted at build time */
  headline: string;
  kicker: string;
  /** Plain-text copy for the OS share sheet; attribution is appended in code */
  shareText: string;
  /**
   * Amount-bearing copy for the explicit 'detailed' mode ({{amount}} available).
   * Only templates whose moments carry a sensible dollar figure define these;
   * everything else falls back to the private copy. Rendered with the
   * 'detailed' scrub mode (amounts preserved).
   */
  detailedKicker?: string;
  detailedShareText?: string;
  tone: ShareCardTone;
  /** When false the share affordance is hidden for this moment entirely */
  active: boolean;
}

/** Code-owned metadata for the admin UI and previews (not stored in Firestore). */
export interface ShareCardTemplateDef extends ShareCardTemplateContent {
  id: ShareCardTemplateId;
  label: string;
  group: 'Insights' | 'Goals' | 'Pause';
  /** Variables available to this template's copy */
  variables: string[];
  /** Sample values used by the admin live preview */
  sampleVars: Record<string, string | number>;
}

export type ShareCardTemplates = Record<ShareCardTemplateId, ShareCardTemplateContent>;

const GOAL_GROUP = 'Goals' as const;

export const SHARE_CARD_TEMPLATE_DEFS: ShareCardTemplateDef[] = [
  {
    id: 'insight_streak',
    label: 'Reflection streak',
    group: 'Insights',
    variables: ['count'],
    sampleVars: { count: 21 },
    eyebrow: 'A quiet streak',
    headline: '{{count}} days of\nnoticing.',
    kicker: 'One small habit, showing up daily.',
    shareText: '{{count}} days of noticing in a row.',
    tone: 'celebrate',
    active: true,
  },
  {
    id: 'insight_spend_comparison',
    label: 'Spending down (month over month)',
    group: 'Insights',
    variables: ['category', 'percent'],
    sampleVars: { category: 'coffee', percent: 32 },
    eyebrow: 'A quiet shift',
    headline: 'A lighter month\nfor {{category}}.',
    kicker: 'No rules, no guilt. Just noticing, and it shows.',
    shareText: 'My {{category}} spending is {{percent}}% lighter than last month.',
    detailedKicker: 'About {{amount}} lighter than last month.',
    detailedShareText: 'My {{category}} spending is about {{amount}} lighter than last month.',
    tone: 'gentle',
    active: true,
  },
  {
    id: 'insight_milestone',
    label: 'Milestone',
    group: 'Insights',
    variables: ['value', 'label'],
    sampleVars: { value: 100, label: 'reflections' },
    eyebrow: 'Milestone',
    headline: '{{value}} {{label}}.\nOne small habit.',
    kicker: 'Quiet pauses, chosen on purpose.',
    shareText: '{{value}} {{label}}.',
    tone: 'celebrate',
    active: true,
  },
  {
    id: 'insight_rank',
    label: 'Community rank',
    group: 'Insights',
    variables: ['percent'],
    sampleVars: { percent: 12 },
    eyebrow: 'A small win',
    headline: 'Top {{percent}}%\nfor reflecting.',
    kicker: 'Not loud about it. Just a few quiet minutes a day.',
    shareText: 'Top {{percent}}% for reflecting this month.',
    tone: 'social',
    active: true,
  },
  {
    id: 'goal_category_spend',
    label: 'Goal met — category spending',
    group: GOAL_GROUP,
    variables: [],
    sampleVars: {},
    eyebrow: 'A goal, met',
    headline: 'I set a spending\nintention. I kept it.',
    kicker: 'Less of what I regret, more of what matters.',
    shareText: 'I set a goal on enough. I met it.',
    detailedKicker: 'About {{amount}} under my old pace.',
    detailedShareText: 'I set a spending goal on enough and came in about {{amount}} under.',
    tone: 'celebrate',
    active: true,
  },
  {
    id: 'goal_total_spend',
    label: 'Goal met — total spending',
    group: GOAL_GROUP,
    variables: [],
    sampleVars: {},
    eyebrow: 'A goal, met',
    headline: 'I set a spending\nintention. I kept it.',
    kicker: 'Less of what I regret, more of what matters.',
    shareText: 'I set a goal on enough. I met it.',
    detailedKicker: 'About {{amount}} under my old pace.',
    detailedShareText: 'I set a spending goal on enough and came in about {{amount}} under.',
    tone: 'celebrate',
    active: true,
  },
  {
    id: 'goal_total_debt',
    label: 'Goal met — total debt down',
    group: GOAL_GROUP,
    variables: [],
    sampleVars: {},
    eyebrow: 'A goal, met',
    headline: 'Debt, moving in\none direction.',
    kicker: 'Down. Steadily. On purpose.',
    shareText: 'I set a goal on enough. I met it.',
    detailedKicker: 'About {{amount}} of debt, down.',
    detailedShareText: 'I set a debt goal on enough and paid down about {{amount}}.',
    tone: 'celebrate',
    active: true,
  },
  {
    id: 'goal_specific_debt_payoff',
    label: 'Goal met — debt payoff',
    group: GOAL_GROUP,
    variables: [],
    sampleVars: {},
    eyebrow: 'A goal, met',
    headline: 'Debt, moving in\none direction.',
    kicker: 'Down. Steadily. On purpose.',
    shareText: 'I set a goal on enough. I met it.',
    detailedKicker: 'About {{amount}} of debt, down.',
    detailedShareText: 'I set a debt goal on enough and paid down about {{amount}}.',
    tone: 'celebrate',
    active: true,
  },
  {
    id: 'goal_no_new_debt',
    label: 'Goal met — no new debt',
    group: GOAL_GROUP,
    variables: [],
    sampleVars: {},
    eyebrow: 'A goal, met',
    headline: 'No new debt.\nOn purpose.',
    kicker: 'A clean stretch, kept clean.',
    shareText: 'I set a goal on enough. I met it.',
    tone: 'celebrate',
    active: true,
  },
  {
    id: 'goal_regret_ratio',
    label: 'Goal met — regret ratio',
    group: GOAL_GROUP,
    variables: [],
    sampleVars: {},
    eyebrow: 'A goal, met',
    headline: 'More spent on\nwhat matters.',
    kicker: 'Fewer regrets. Same money, better fit.',
    shareText: 'I set a goal on enough. I met it.',
    tone: 'celebrate',
    active: true,
  },
  {
    id: 'goal_app_sessions',
    label: 'Goal met — showing up',
    group: GOAL_GROUP,
    variables: [],
    sampleVars: {},
    eyebrow: 'A goal, met',
    headline: 'I kept showing\nup for myself.',
    kicker: 'Small check-ins, real practice.',
    shareText: 'I set a goal on enough. I met it.',
    tone: 'celebrate',
    active: true,
  },
  {
    id: 'goal_emergency_fund',
    label: 'Goal met — emergency fund',
    group: GOAL_GROUP,
    variables: [],
    sampleVars: {},
    eyebrow: 'A goal, met',
    headline: 'A cushion,\nquietly growing.',
    kicker: 'Future me says thanks.',
    shareText: 'I set a goal on enough. I met it.',
    detailedKicker: 'About {{amount}} set aside.',
    detailedShareText: 'I set a savings goal on enough and set aside about {{amount}}.',
    tone: 'celebrate',
    active: true,
  },
  {
    id: 'goal_monthly_savings',
    label: 'Goal met — monthly savings',
    group: GOAL_GROUP,
    variables: [],
    sampleVars: {},
    eyebrow: 'A goal, met',
    headline: 'A cushion,\nquietly growing.',
    kicker: 'Future me says thanks.',
    shareText: 'I set a goal on enough. I met it.',
    detailedKicker: 'About {{amount}} set aside.',
    detailedShareText: 'I set a savings goal on enough and set aside about {{amount}}.',
    tone: 'celebrate',
    active: true,
  },
  {
    id: 'goal_life_event',
    label: 'Goal met — life event',
    group: GOAL_GROUP,
    variables: [],
    sampleVars: {},
    eyebrow: 'A goal, met',
    headline: 'Saving toward\nsomething real.',
    kicker: 'On track, one month at a time.',
    shareText: 'I set a goal on enough. I met it.',
    detailedKicker: 'About {{amount}} set aside.',
    detailedShareText: 'I set a savings goal on enough and set aside about {{amount}}.',
    tone: 'celebrate',
    active: true,
  },
  {
    id: 'pause_breaks',
    label: 'Pause — shopping breaks taken',
    group: 'Pause',
    variables: ['count', 'noun'],
    sampleVars: { count: 3, noun: 'breaks' },
    eyebrow: 'The pause',
    headline: '{{count}} shopping {{noun}},\nactually taken.',
    kicker: 'A beat between impulse and action.',
    shareText: '{{count}} shopping {{noun}}, actually taken.',
    tone: 'gentle',
    active: true,
  },
  {
    id: 'pause_dismissed',
    label: 'Pause — items let go',
    group: 'Pause',
    variables: ['count', 'noun'],
    sampleVars: { count: 4, noun: 'things' },
    eyebrow: 'The pause',
    headline: '{{count}} {{noun}} I paused on,\nthen let go.',
    kicker: 'Wanted it less after the wait.',
    shareText: '{{count}} {{noun}} I paused on, then let go.',
    detailedKicker: 'About {{amount}} left unspent.',
    detailedShareText: '{{count}} {{noun}} I paused on, then let go — about {{amount}} unspent.',
    tone: 'gentle',
    active: true,
  },
];

export const DEFAULT_SHARE_CARD_TEMPLATES: ShareCardTemplates = Object.fromEntries(
  SHARE_CARD_TEMPLATE_DEFS.map((d) => [
    d.id,
    {
      eyebrow: d.eyebrow,
      headline: d.headline,
      kicker: d.kicker,
      shareText: d.shareText,
      // Only present on amount-bearing templates — never emit undefined keys
      // (Firestore setDoc rejects undefined values on admin save).
      ...(d.detailedKicker ? { detailedKicker: d.detailedKicker } : {}),
      ...(d.detailedShareText ? { detailedShareText: d.detailedShareText } : {}),
      tone: d.tone,
      active: d.active,
    },
  ])
) as ShareCardTemplates;

/**
 * Strip anything that looks like a currency amount — defense-in-depth for
 * both builder variables and admin-authored copy. 'private' (the default)
 * strips $-amounts; 'detailed' is the explicit user opt-in that preserves
 * them (whitespace is still normalized). The default parameter keeps every
 * existing call site private-by-default — do NOT change it.
 */
export const scrubAmounts = (text: string, mode: ShareAmountMode = 'private'): string => {
  const scrubbed =
    mode === 'detailed'
      ? text
      : text
          .replace(/\$\s?\d[\d,]*(\.\d+)?/g, '')
          .replace(/\d[\d,]*(\.\d+)?\s?(dollars|usd)/gi, '');
  return scrubbed.replace(/[^\S\n]{2,}/g, ' ').trim();
};

/** Substitute {{variables}} into a template string and scrub amounts per mode. */
export const renderShareTemplate = (
  template: string,
  vars: Record<string, string | number>,
  mode: ShareAmountMode = 'private'
): string =>
  scrubAmounts(
    template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key: string) =>
      key in vars ? String(vars[key]) : ''
    ),
    mode
  );

const VALID_TONES: ShareCardTone[] = ['note', 'celebrate', 'gentle', 'social'];
const MAX_FIELD_LENGTH = 200;

/** Merge a raw Firestore override onto the default, ignoring malformed fields. */
export function mergeTemplateOverride(
  base: ShareCardTemplateContent,
  raw: Record<string, unknown> | undefined
): ShareCardTemplateContent {
  if (!raw) return base;
  const str = (v: unknown, fallback: string): string =>
    typeof v === 'string' && v.trim().length > 0 && v.length <= MAX_FIELD_LENGTH ? v : fallback;
  const strOpt = (v: unknown, fallback: string | undefined): string | undefined =>
    typeof v === 'string' && v.trim().length > 0 && v.length <= MAX_FIELD_LENGTH ? v : fallback;
  const detailedKicker = strOpt(raw.detailedKicker, base.detailedKicker);
  const detailedShareText = strOpt(raw.detailedShareText, base.detailedShareText);
  return {
    eyebrow: str(raw.eyebrow, base.eyebrow),
    headline: str(raw.headline, base.headline),
    kicker: str(raw.kicker, base.kicker),
    shareText: str(raw.shareText, base.shareText),
    ...(detailedKicker !== undefined ? { detailedKicker } : {}),
    ...(detailedShareText !== undefined ? { detailedShareText } : {}),
    tone: VALID_TONES.includes(raw.tone as ShareCardTone) ? (raw.tone as ShareCardTone) : base.tone,
    active: typeof raw.active === 'boolean' ? raw.active : base.active,
  };
}
